import { memo, useState } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/Input';
import { Sheet } from '@/components/Sheet';
import { cn } from '@/lib/cn';
import { newIdempotencyKey } from '@/lib/ids';
import { formatParty } from '@/venue/venue';
import type { KitchenItem, KitchenTicket } from './api';
import { useClock } from './clock';
import { useAdvanceItems, useReprintTicket, useVoidItem } from './queries';
import {
  agingOf,
  describeKitchenError,
  formatElapsed,
  linesIn,
  locationText,
  locationTypeOf,
  nextStep,
  statusView,
  ticketNumber,
  waitingText,
} from './wording';
import type { BoardStatus } from './wording';
import { cardClasses } from '@/components/Card';

const AGING_TEXT = {
  normal: 'text-ink',
  attention: 'text-attention',
  late: 'text-danger',
} as const;
const AGING_BAR = {
  normal: 'bg-line-strong',
  attention: 'bg-attention',
  late: 'bg-danger',
} as const;

/** Seconds since submission: the server's figure plus the time since it was received. Display only. */
function waited(seconds: number, receivedAt: number): number {
  return seconds + Math.max(0, (Date.now() - receivedAt) / 1000);
}

/** Only this component (and `AgingBar`) re-renders when the clock ticks. */
function Elapsed({ seconds, receivedAt }: { seconds: number; receivedAt: number }) {
  useClock();
  const total = waited(seconds, receivedAt);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-lg font-semibold tabular-nums',
        AGING_TEXT[agingOf(total)],
      )}
    >
      <Icon name="orders" size={18} />
      <span className="sr-only">Waiting </span>
      {formatElapsed(total)}
    </span>
  );
}

function AgingBar({ seconds, receivedAt }: { seconds: number; receivedAt: number }) {
  useClock();
  return (
    <span
      aria-hidden="true"
      className={cn(
        'absolute inset-y-0 left-0 w-1.5',
        AGING_BAR[agingOf(waited(seconds, receivedAt))],
      )}
    />
  );
}

/** "10:42" in the device's own time format. */
export function formatClock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ─── Sheets ──────────────────────────────────────────────────────────────────

/** Owned by the board, not the card: a refusal must stay readable even if the line leaves the tab. */
export function VoidSheet({
  item,
  ticket,
  onClose,
}: {
  item: KitchenItem;
  ticket: KitchenTicket;
  onClose: () => void;
}) {
  const voidItem = useVoidItem();
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const failure = voidItem.isError ? describeKitchenError(voidItem.error) : null;

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const code = reason.trim();
    if (!code) {
      setReasonError('Enter a reason.');
      return;
    }
    setReasonError(null);
    const trimmedNotes = notes.trim();
    voidItem.mutate(
      {
        itemId: item.order_item_id,
        itemName: item.name,
        input: { reason_code: code, ...(trimmedNotes ? { notes: trimmedNotes } : {}) },
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Sheet open onClose={onClose} title="Void this item?">
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <p className="text-base text-ink">
          <span className="font-semibold tabular-nums">{item.quantity}×</span> {item.name} ·{' '}
          {locationText(ticket.location_type, ticket.location_label)} ·{' '}
          {ticketNumber(ticket.order_number)}
        </p>
        <Input
          label="Reason"
          value={reason}
          maxLength={50}
          onChange={(event) => setReason(event.target.value)}
          error={reasonError}
          autoComplete="off"
        />
        <Input
          label="Notes (optional)"
          value={notes}
          maxLength={200}
          onChange={(event) => setNotes(event.target.value)}
          autoComplete="off"
        />
        <p className="text-sm text-muted">
          The whole line is removed from the order. This is recorded against your name.
        </p>
        {failure && (
          <p role="alert" className="text-sm font-medium text-danger">
            {failure}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={voidItem.isPending}>
            Void item
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

// ─── Ticket ──────────────────────────────────────────────────────────────────

/**
 * One line of a ticket. Everything a cook needs at arm's length sits on one row: the quantity and dish
 * large and bold on the left, the state and the one next action on the right. Choices and the guest's
 * note hang under the name, the note on its own amber line so it cannot be missed.
 */
function ItemRow({
  item,
  ticket,
  canVoid,
  busy,
  pending,
  onAdvance,
  onVoid,
}: {
  item: KitchenItem;
  ticket: KitchenTicket;
  canVoid: boolean;
  /** Something on this ticket is being saved. */
  busy: boolean;
  /** This very item is being saved. */
  pending: boolean;
  onAdvance: (item: KitchenItem, to: 'IN_PREPARATION' | 'READY') => void;
  onVoid: (item: KitchenItem) => void;
}) {
  const status = statusView(item.fulfillment_status, ticket.location_type);
  const step = nextStep(item.fulfillment_status);
  const served = item.fulfillment_status === 'SERVED';
  const waiting = item.fulfillment_status === 'READY';

  return (
    <li className={cn('flex items-start gap-3 py-2.5', served && 'opacity-70')}>
      <span className="min-w-9 text-xl leading-7 font-bold text-ink tabular-nums">
        {item.quantity}×
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-lg leading-7 font-bold break-words text-ink">{item.name}</p>
        {item.modifiers.length > 0 && (
          <ul className="mt-0.5 flex flex-wrap gap-x-3 text-base leading-6 text-ink">
            {item.modifiers.map((modifier, index) => (
              <li key={`${modifier}-${index}`} className="break-words">
                {modifier}
              </li>
            ))}
          </ul>
        )}
        {item.notes && (
          <p className="mt-1.5 inline-block rounded-control bg-attention-soft px-2.5 py-1 text-base leading-6 font-semibold break-words text-attention-ink">
            Note: {item.notes}
          </p>
        )}
        {waiting && (
          <p className="mt-1 text-sm font-medium text-success">
            {waitingText(ticket.location_type)}
          </p>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        <Badge tone={status.tone}>{status.label}</Badge>
        {canVoid && !served && (
          <Button
            variant="destructive"
            disabled={busy}
            onClick={() => onVoid(item)}
            aria-label={`Void ${item.name}`}
          >
            Void
          </Button>
        )}
        {step && (
          <Button
            variant="primary"
            loading={pending}
            disabled={busy}
            aria-label={`${step.label} ${item.name}`}
            onClick={() => onAdvance(item, step.to)}
          >
            {step.label}
          </Button>
        )}
      </div>
    </li>
  );
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * One order on one tab. `shown` is the tab's state: only the lines in that state are listed, with the
 * whole-ticket action for that state in the footer. The header is the same on every tab, so a cook can
 * tell the table, number, guest and age wherever the order appears. Reprint always prints the whole ticket.
 * With no `shown` (a status this board does not know) every line is listed.
 */
export const TicketCard = memo(function TicketCard({
  ticket,
  receivedAt,
  canVoid,
  shown,
  onVoid,
}: {
  ticket: KitchenTicket;
  /** When the board data arrived (ms): anchors the server's elapsed time. */
  receivedAt: number;
  canVoid: boolean;
  shown?: BoardStatus;
  /** Opens the board's void sheet for one line of this ticket. */
  onVoid: (item: KitchenItem, ticket: KitchenTicket) => void;
}) {
  const advance = useAdvanceItems();
  const reprint = useReprintTicket();

  const locationType = locationTypeOf(ticket.location_type);
  const pendingIds = new Set(
    advance.isPending ? advance.variables.items.map((item) => item.order_item_id) : [],
  );
  const lines = shown ? linesIn(ticket, shown) : ticket.items;
  const elsewhere = ticket.items.length - lines.length;

  const bulk =
    shown === 'SUBMITTED' && lines.length > 1
      ? { label: `Start all (${lines.length})`, to: 'IN_PREPARATION' as const }
      : shown === 'IN_PREPARATION' && lines.length > 1
        ? { label: `Mark all ready (${lines.length})`, to: 'READY' as const }
        : null;

  function advanceItems(items: KitchenItem[], to: 'IN_PREPARATION' | 'READY') {
    advance.mutate({ items, to });
  }

  function reprintTicket() {
    reprint.mutate({ orderId: ticket.order_id, idempotencyKey: newIdempotencyKey() });
  }

  const title = locationText(ticket.location_type, ticket.location_label);
  const titleId = `ticket-${ticket.order_id}`;

  return (
    <article
      aria-labelledby={titleId}
      className={cn(
        cardClasses({ padding: 'none' }),
        'relative flex flex-col gap-1 overflow-hidden py-3 pr-3 pl-4',
      )}
    >
      <AgingBar seconds={ticket.elapsed_seconds} receivedAt={receivedAt} />
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id={titleId} className="text-xl leading-6 font-bold break-words text-ink">
            {title}
          </h3>
          <p className="mt-0.5 text-sm break-words text-muted">
            {ticketNumber(ticket.order_number)} · {formatParty(locationType, ticket.party_name)}
            {ticket.zone_name ? ` · ${ticket.zone_name}` : ''}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-0.5">
          <Elapsed seconds={ticket.elapsed_seconds} receivedAt={receivedAt} />
          {elsewhere > 0 && (
            <p className="text-xs text-muted">
              {plural(lines.length, 'line')} here · {elsewhere} on other tabs
            </p>
          )}
        </div>
      </header>

      {ticket.notes && (
        <p className="mt-2 rounded-control bg-attention-soft px-3 py-1.5 text-base leading-6 break-words text-attention-ink">
          <span className="font-semibold">Order note:</span> {ticket.notes}
        </p>
      )}

      <ul className="mt-1 flex flex-col divide-y divide-line">
        {lines.map((item) => (
          <ItemRow
            key={item.order_item_id}
            item={item}
            ticket={ticket}
            canVoid={canVoid}
            busy={advance.isPending}
            pending={pendingIds.has(item.order_item_id)}
            onAdvance={(one, to) => advanceItems([one], to)}
            onVoid={(one) => onVoid(one, ticket)}
          />
        ))}
      </ul>

      {/* The same footer on every card: the whole-ticket action (or the line count) left, reprint right. */}
      <footer className="mt-1 flex min-h-11 flex-wrap items-center gap-2 border-t border-line pt-2">
        {bulk ? (
          <Button
            variant="secondary"
            disabled={advance.isPending}
            onClick={() => advanceItems(lines, bulk.to)}
          >
            {bulk.label}
          </Button>
        ) : (
          <span className="px-1 text-sm text-muted tabular-nums">
            {plural(lines.length, 'line')}
          </span>
        )}
        <Button
          variant="tertiary"
          className="ml-auto"
          loading={reprint.isPending}
          onClick={reprintTicket}
          aria-label={`Reprint ticket ${ticketNumber(ticket.order_number)}`}
        >
          Reprint ticket
        </Button>
      </footer>
    </article>
  );
});
